import React from 'react';

export type FullReportType =
  | 'CHEMICAL'
  | 'TENSILE'
  | 'CVN'
  | 'AUT'
  | 'HYDROSTATIC'
  | 'DIMENSIONAL'
  | 'HARDNESS'
  | 'FLATTENING'
  | 'BEND'
  | 'VISUAL'
  | 'RT'
  | 'MPT'
  | 'HEAT_TREAT'
  | 'PIPE_BODY_UT'
  | 'MFL_ET'
  | 'COATING'
  | 'THREAD'
  | 'WELD_MAP';

export type ReportSummary = {
  status: string;
  updated?: string | null;
  source?: string | null;
  note?: string;
};

export type ReportDefinition = {
  key: FullReportType;
  label: string;
  freq: string;
  psl2Mandatory?: boolean;
};

export type ProjectLimitFieldOption = {
  fieldPath: string;
  symbol?: string;
  label: string;
  unit: string;
  limit: string;
  min?: number | null;
  max?: number | null;
};

export type ReportMetadataEntry = {
  reportId?: string;
  performedAt?: string;
  lab?: string;
  technician?: string;
  source?: string;
  sourceFile?: string;
  note?: string;
  frequency?: string;
};

export type AdjudicationRequest = {
  status: string;
  action: string;
  note?: string;
};

export const ReportAdjudicationContext = React.createContext<{
  reportType?: FullReportType;
  status?: string;
  note?: string;
  isSaving?: boolean;
  onAdjudicate?: (request: AdjudicationRequest) => void;
}>({});


export type StandardCheck = {
  key: string;
  label: string;
  value: number;
  unit: string;
  min?: number;
  max?: number;
  status: string;
};

export type StandardObservation = {
  id: string;
  area: string;
  finding: string;
  disposition: string;
  status: string;
};

export type StandardReportDetailData = {
  reportId: string;
  title: string;
  subtitle: string;
  status: string;
  performedAt: string;
  owner: string;
  station: string;
  source: string;
  sourceFile: string;
  spec: string;
  summary: string;
  checks: StandardCheck[];
  observations: StandardObservation[];
  limits: Array<[string, string]>;
};


export type ReportMeasurementItem = {
  id: string;
  reportType: FullReportType;
  fieldPath: string;
  fieldLabel: string;
  fieldSymbol?: string;
  measuredValue: string;
  unit: string;
  frequency: string;
  limit: string;
  source: string;
  reference: string;
  status: string;
  tensileSpecimenId?: string;
  tensileLocation?: string;
  tensileOrientation?: string;
  tensileAreaMm2?: string;
  tensileYsMpa?: string;
  tensileUtsMpa?: string;
  tensileElongationPct?: string;
  tensileYsUtsRatio?: string;
  cvnSpecimenId?: string;
  cvnLocation?: string;
  cvnNotchOrientation?: string;
  cvnSpecimenSize?: string;
  cvnEnergyJ?: string;
  cvnShearPct?: string;
  cvnLateralExpansionMm?: string;
  autIndicationId?: string;
  autPositionMm?: string;
  autClock?: string;
  autFlawType?: string;
  autDepthPctWt?: string;
  autLengthMm?: string;
  autDisposition?: string;
  hydroTestPressureBar?: string;
  hydroHoldDurationS?: string;
  hydroPressureDropObserved?: string;
  hydroLeakObserved?: string;
  hydroMedium?: string;
  hydroSealingMethod?: string;
  hydroGaugeCalRef?: string;
  hydroDesignFactor?: string;
  dimOdAvgMm?: string;
  dimOdMinMm?: string;
  dimOdMaxMm?: string;
  dimOvalityPct?: string;
  dimWtEnd1N?: string;
  dimWtEnd1E?: string;
  dimWtEnd1S?: string;
  dimWtEnd1W?: string;
  dimWtEnd2N?: string;
  dimWtEnd2E?: string;
  dimWtEnd2S?: string;
  dimWtEnd2W?: string;
  dimLengthM?: string;
  dimStraightnessMm?: string;
  dimBevelAngleDeg?: string;
  dimEndSquarenessMm?: string;
  flatteningSpecimenId?: string;
  flatteningWeldPosition?: string;
  flatteningOdBeforeMm?: string;
  flatteningHdReached?: string;
  flatteningLimit?: string;
  flatteningCracksObserved?: string;
  bendSpecimenId?: string;
  bendType?: string;
  bendAngleDeg?: string;
  bendCrackLengthMm?: string;
  bendFracture?: string;
  bendWallThicknessMm?: string;
  bendMultiplier?: string;
  bendRequiredFormerMm?: string;
  bendMillFormerMm?: string;
  hardnessZone?: string;
  hardnessReadingId?: string;
  hardnessPosition?: string;
  hardnessValueHv10?: string;
  hardnessProjectLimitHv10?: string;
  hardnessFallbackLimitHv10?: string;
  visualDefectId?: string;
  visualDefectType?: string;
  visualPositionMm?: string;
  visualClock?: string;
  visualDepthPctWt?: string;
  visualDisposition?: string;
  visualLightingLux?: string;
  rtIndicationId?: string;
  rtPositionMm?: string;
  rtFlawType?: string;
  rtLengthMm?: string;
  rtAreaMm2?: string;
  rtDisposition?: string;
  rtIqiSensitivityPct?: string;
  rtExposureClass?: string;
  mptIndicationId?: string;
  mptEnd?: string;
  mptType?: string;
  mptLengthMm?: string;
  mptDisposition?: string;
  mptFieldStrengthOe?: string;
  mptUvIrradiance?: string;
  heatBatchId?: string;
  heatAustenitisingTempC?: string;
  heatAustenitisingHoldMin?: string;
  heatCoolingRateCps?: string;
  heatTemperingTempC?: string;
  heatTemperingHoldMin?: string;
  heatPipesInBatch?: string;
  pbutIndicationId?: string;
  pbutIndicationType?: string;
  pbutPositionMm?: string;
  pbutClock?: string;
  pbutAreaMm2?: string;
  pbutDepthPctWt?: string;
  pbutEndZone?: string;
  pbutDisposition?: string;
  mflIndicationId?: string;
  mflFlawType?: string;
  mflSignalPctRef?: string;
  mflDisposition?: string;
  mflCalNotchPctWt?: string;
  mflRoute?: string;
  coatingType?: string;
  coatingDftMinUm?: string;
  coatingDftAvgUm?: string;
  coatingAdhesionNcm?: string;
  coatingCdRadiusMm?: string;
  coatingAnchorProfileUm?: string;
  coatingHolidayVoltageKv?: string;
  coatingHolidaysFound?: string;
  coatingCutbackLengthMm?: string;
  coatingSurfacePrepGrade?: string;
  threadType?: string;
  threadStandard?: string;
  threadGaugeSetRef?: string;
  threadEndTested?: string;
  threadCouplingApplied?: string;
  threadVisualDamage?: string;
  threadTaperMmPerM?: string;
  threadLeadMmCum?: string;
  threadHeightMm?: string;
  threadPipeEndDiameterMm?: string;
  weldPassName?: string;
  weldProcess?: string;
  weldWireHeatNo?: string;
  weldFluxLotNo?: string;
  weldCurrentA?: string;
  weldVoltageV?: string;
  weldSpeedMmMin?: string;
  weldHeatInputKjMm?: string;
  weldPlateId?: string;
  weldPlateHeatNumber?: string;
  weldPipeHeatNumber?: string;
  weldWpsRef?: string;
};

export type ChemicalElementDisplay = {
  id: string;
  symbol: string;
  name: string;
  value: number;
  displayValue: string;
  unit: string;
  limit: string;
  max?: number;
  itpStricter?: boolean;
  stdMax?: number;
  storeOnly?: boolean;
  status: string;
  source: 'sample' | 'user';
  measurement?: ReportMeasurementItem;
};